import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Button,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeviceHeartbeatFilter from "../component/DeviceHeartbeatFilter";
import DeviceHeartbeatService from "../../../services/DeviceHeartbeatServices/DeviceHeartbeatService";

// Utility function to export data to CSV
const exportToCSV = (data, filename = "device-logs.csv") => {
  if (!data || data.length === 0) {
    toast.error("No data to export");
    return;
  }

  // Define CSV header
  const headers = [
    "SN",
    "MAC ID",
    "Application",
    "Version",
    "Status",
    "Machine Name",
    "Local IP",
    "Heartbeat Time",
  ];

  // Convert data to CSV rows
  const csvContent = [
    headers.join(","),
    ...data.map((row, idx) =>
      [
        idx + 1,
        row.macId || "-",
        row.applicationName || "-",
        row.appVersion || "-",
        row.isAppRunning ? "Running" : "Stopped",
        row.machineName || "-",
        row.localIp || "-",
        new Date(row.heartbeatAt).toLocaleString(),
      ].join(","),
    ),
  ].join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  toast.success("Data exported successfully!");
};

// Custom hook for smooth polling without page refresh
const useHeartbeatLogs = (
  macId,
  liveMode,
  pageNumber = 1,
  pageSize = 10,
  origin = null,
) => {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 1,
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);

  const intervalRef = useRef(null);
  const prevDataRef = useRef([]);
  const paramsRef = useRef({ macId, pageNumber, pageSize, origin });

  // Update params ref without triggering effects
  paramsRef.current = { macId, pageNumber, pageSize, origin };

  const fetchLogs = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) {
        setIsInitialLoad(true);
      } else {
        setIsFetching(true);
      }

      const response = await DeviceHeartbeatService.getHeartbeatLogs(
        paramsRef.current.macId,
        paramsRef.current.origin,
        paramsRef.current.pageNumber,
        paramsRef.current.pageSize,
      );

      // Handle paginated response
      if (response && response.data) {
        // Only update if data actually changed
        const dataString = JSON.stringify(response.data);
        const prevString = JSON.stringify(prevDataRef.current);

        if (dataString !== prevString) {
          setData(response.data);
          prevDataRef.current = response.data;
        }

        setPagination({
          pageNumber: response.pageNumber || paramsRef.current.pageNumber,
          pageSize: response.pageSize || paramsRef.current.pageSize,
          totalRecords: response.totalRecords || 0,
          totalPages: response.totalPages || 1,
        });
      } else {
        setData([]);
        prevDataRef.current = [];
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching logs:", err);
      setError(err.message);
      toast.error("Failed to fetch heartbeat logs");
    } finally {
      if (showLoading) {
        setIsInitialLoad(false);
      } else {
        setIsFetching(false);
      }
    }
  }, []);

  // Fetch immediately when macId or origin changes
  useEffect(() => {
    fetchLogs(true);
  }, [macId, origin, pageNumber, pageSize, fetchLogs]);

  // Set up polling interval based on liveMode
  useEffect(() => {
    // Set up polling for continuous updates
    const interval = liveMode ? 8000 : 20000; // 8s for live, 20s for normal

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      fetchLogs(false); // Fetch without showing full loading state
    }, interval);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [liveMode, fetchLogs]);

  return {
    data,
    pagination,
    isInitialLoad,
    isFetching,
    error,
    refetch: () => fetchLogs(false),
  };
};

// Format date time nicely
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// Calculate time difference from now
const getTimeDifference = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 60) return `${diffSecs}s ago`;
  if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;
  if (diffSecs < 86400) return `${Math.floor(diffSecs / 3600)}h ago`;
  return `${Math.floor(diffSecs / 86400)}d ago`;
};

const DeviceHeartbeatLogs = () => {
  const [filters, setFilters] = useState({
    macId: "",
    applicationName: "",
    isAppRunning: null,
    liveMode: true,
    origin: null,
  });

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data, pagination, isInitialLoad, isFetching, error, refetch } =
    useHeartbeatLogs(
      filters.macId,
      filters.liveMode,
      pageNumber,
      pageSize,
      filters.origin,
    );

  // Apply client-side filters
  const filteredData = Array.isArray(data)
    ? data.filter((log) => {
        // Note: applicationName (origin) filtering is already done on the server side
        // Only apply client-side filters for fields not filtered on the server
        if (
          filters.isAppRunning !== null &&
          log.isAppRunning !== filters.isAppRunning
        ) {
          return false;
        }
        return true;
      })
    : [];

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPageNumber(1); // Reset to first page when filter changes
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPageNumber(1); // Reset to first page when page size changes
  };

  // Custom styles for DataTable
  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #dee2e6",
        padding: "12px 8px",
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
        padding: "8px 6px",
      },
      stripedStyle: {
        backgroundColor: "#f8f9fa",
      },
      stripedAfterStyle: {
        backgroundColor: "white",
      },
    },
    cells: {
      style: {
        padding: "8px 6px",
        overflow: "visible",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #dee2e6",
        backgroundColor: "#f8f9fa",
      },
    },
  };

  // Conditional row styles for green/red highlighting
  const conditionalRowStyles = [
    {
      when: (row) => row.isAppRunning === true,
      style: {
        backgroundColor: "rgba(34, 197, 94, 0.1)", // Light green
        color: "#15803d",
        fontWeight: 500,
        borderLeft: "4px solid #22c55e",
      },
    },
    {
      when: (row) => row.isAppRunning === false,
      style: {
        backgroundColor: "rgba(239, 68, 68, 0.1)", // Light red
        color: "#b91c1c",
        fontWeight: 500,
        borderLeft: "4px solid #ef4444",
      },
    },
  ];

  // DataTable columns configuration
  const columns = [
    {
      name: "SN",
      cell: (row, index) => {
        const currentPage = pagination.pageNumber || 1;
        const currentSize = pagination.pageSize || pageSize;

        return (
          <div className="fw-bold text-primary">
            {(currentPage - 1) * currentSize + index + 1}
          </div>
        );
      },
      width: "70px",
      sortable: false,
      center: true,
    },
    {
      name: "MAC ID",
      selector: (row) => row.macId || "-",
      sortable: true,
      cell: (row) => (
        <code className="text-dark fw-bold text-uppercase">
          {row.macId || "-"}
        </code>
      ),
      wrap: true,
    },
    // {
    //   name: "Application",
    //   selector: (row) => row.applicationName || "-",
    //   sortable: true,
    //   wrap: true,
    //   cell: (row) => <span className="fw-500">{row.applicationName || "-"}</span>,
    // },
    // {
    //   name: "Version",
    //   selector: (row) => row.appVersion || "-",
    //   sortable: true,
    //   cell: (row) => (
    //     <Badge pill color="info">
    //       v{row.appVersion || "-"}
    //     </Badge>
    //   ),
    //   width: "110px",
    //   center: true,
    // },
    {
      name: "Machine Name",
      selector: (row) => row.machineName || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Status",
      selector: (row) => (row.isAppRunning ? "Running" : "Stopped"),
      sortable: true,
      cell: (row) => (
        <Badge color={row.isAppRunning ? "success" : "danger"}>
          {row.isAppRunning ? (
            <>
              <i className="ri-checkbox-circle-fill me-1"></i> Running
            </>
          ) : (
            <>
              <i className="ri-close-circle-fill me-1"></i> Stopped
            </>
          )}
        </Badge>
      ),
      width: "130px",
      center: true,
    },

    {
      name: "Local IP",
      selector: (row) => row.localIp || "-",
      sortable: true,
      cell: (row) => (
        <code className="bg-light d-block p-1">{row.localIp || "-"}</code>
      ),
    },
    {
      name: "Heartbeat Time",
      selector: (row) => formatDateTime(row.heartbeatAt) || "-",
      sortable: true,
      wrap: true,
      cell: (row) => (
        <small className="text-muted">
          {formatDateTime(row.heartbeatAt) || "-"}
        </small>
      ),
    },
    {
      name: "Time Ago",
      selector: (row) => getTimeDifference(row.heartbeatAt),
      sortable: false,
      cell: (row) => {
        const timeDiff = getTimeDifference(row.heartbeatAt);
        const isRecent = timeDiff.includes("s") || timeDiff.includes("m");
        return (
          <Badge color={isRecent ? "success" : "warning"}>
            <i className="ri-time-line me-1"></i>
            {timeDiff}
          </Badge>
        );
      },
      width: "100px",
      center: true,
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Device Heartbeat Logs"
            pageTitle="Device Heartbeat Logs"
          />

          {/* Filter Card */}
          <DeviceHeartbeatFilter
            onFilterChange={handleFilterChange}
            isLoading={isInitialLoad}
          />

          {/* Data Table */}
          <Row>
            <Col lg="12">
              <Card className="border-0 shadow-sm position-relative">
                <CardHeader className="bg-light">
                  <Row className="align-items-center">
                    <Col>
                      <h5 className="mb-0">
                        Device Heartbeat Logs{" "}
                        <Badge color={filters.liveMode ? "success" : "info"}>
                          {filters.liveMode ? "LIVE" : "NORMAL"}
                        </Badge>{" "}
                        <small className="text-muted">
                          ({pagination.totalRecords} total)
                        </small>
                        {isFetching && (
                          <span className="ms-2">
                            <span
                              className="spinner-border spinner-border-sm text-primary"
                              aria-hidden="true"
                            ></span>
                          </span>
                        )}
                      </h5>
                    </Col>
                    <Col className="text-end">
                      <div className="btn-group" role="group">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={refetch}
                          disabled={isInitialLoad}
                          title="Refresh data"
                        >
                          <i className="ri-refresh-line"></i> Refresh
                        </button>
                        <button
                          className="btn btn-sm btn-outline-success me-2"
                          onClick={() =>
                            exportToCSV(
                              filteredData,
                              `device-logs-${
                                new Date().toISOString().split("T")[0]
                              }.csv`,
                            )
                          }
                          disabled={filteredData.length === 0}
                          title="Export to CSV"
                        >
                          <i className="ri-download-line"></i> Export CSV
                        </button>
                      </div>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody
                  style={{
                    position: "relative",
                    opacity: isFetching ? 0.7 : 1,
                    transition: "opacity 0.3s ease",
                  }}
                >
                  {error && (
                    <div className="alert alert-danger mb-3" role="alert">
                      <i className="ri-alert-line"></i> Error: {error}
                    </div>
                  )}

                  {isInitialLoad && filteredData.length === 0 ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "40px 0",
                      }}
                    >
                      <Triangle color="#435ebe" height={80} width={80} />
                    </div>
                  ) : (
                    <div
                      className="table-responsive"
                      style={{ transition: "opacity 0.3s ease" }}
                    >
                      <DataTable
                        columns={columns}
                        data={filteredData}
                        pagination
                        paginationServer
                        paginationTotalRows={pagination.totalRecords}
                        onChangePage={(page) => handlePageChange(page)}
                        onChangeRowsPerPage={(newPerPage, page) => {
                          handlePageSizeChange(newPerPage);
                        }}
                        paginationPerPage={pageSize}
                        paginationRowsPerPageOptions={[
                          10, 20, 50, 100, 500, 1000,
                        ]}
                        progressPending={false}
                        customStyles={customStyles}
                        conditionalRowStyles={conditionalRowStyles}
                        noDataComponent={
                          <div className="py-5 text-center w-100">
                            <i className="ri-inbox-line display-4 text-muted"></i>
                            <p className="text-muted mt-3">
                              {isInitialLoad
                                ? "Loading..."
                                : "No heartbeat logs found"}
                            </p>
                          </div>
                        }
                        defaultSortFieldId={8}
                        defaultSortAsc={false}
                        sortServer
                        striped
                        highlightOnHover
                        pointerOnHover
                        responsive
                        dense={false}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DeviceHeartbeatLogs;
